import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
} from "@fluentui/react-components";
import { DatePicker } from "@fluentui/react-datepicker-compat";
import { DismissRegular } from "@fluentui/react-icons";
import { addDays, addMonths, format, isValid, startOfDay, subDays } from "date-fns";
import { useTranslation } from "react-i18next";
import { useAppTheme } from "hooks/useAppTheme";

/** Offered start date: seven days ago */
export const getDefaultIssuedFrom = (): Date =>
  startOfDay(subDays(new Date(), DEFAULT_EXPORT_DAYS));

/** Offered end date: today */
export const getDefaultIssuedTo = (): Date => startOfDay(new Date());

export const DEFAULT_EXPORT_DAYS = 7;

/** Longest period: the end date may be at most one calendar month after the start date, minus a day (1-31 October, not 1 October-1 November) */
export const MAX_EXPORT_PERIOD_MONTHS = 1;

export const getMaxIssuedTo = (issuedFrom: Date): Date =>
  addDays(addMonths(startOfDay(issuedFrom), MAX_EXPORT_PERIOD_MONTHS), -1);

/**
 * The issuedFrom query value: the start of the chosen day in the browser's time zone
 * (Latvia for QTG users), so the backend compares against the same instant.
 */
export const toIssuedFromParam = (date: Date): string =>
  startOfDay(date).toISOString();

/**
 * The issuedTo query value: the whole chosen day, sent as the start of the next day
 * because the backend treats an ISO date-time as an exclusive upper bound.
 */
export const toIssuedToParam = (date: Date): string =>
  startOfDay(addDays(date, 1)).toISOString();

export type ExportInvoicesRange = { issuedFrom: Date; issuedTo: Date };

type Props = {
  open: boolean;
  onCancel: () => void;
  onConfirm: (range: ExportInvoicesRange) => void;
};

/**
 * Asks for the issue date from which the invoices are exported (every MTPL, CASCO and
 * group invoice issued on that day or later), before the export is requested from the backend.
 */
export const ExportInvoicesDialog = ({ open, onCancel, onConfirm }: Props) => {
  const { t } = useTranslation();
  const theme = useAppTheme();
  const [issuedFrom, setIssuedFrom] = useState<Date | null>(null);
  const [issuedTo, setIssuedTo] = useState<Date | null>(null);

  useEffect(() => {
    if (!open) return;
    setIssuedFrom(getDefaultIssuedFrom());
    setIssuedTo(getDefaultIssuedTo());
  }, [open]);

  const validationError = useMemo((): string | null => {
    if (!issuedFrom || !isValid(issuedFrom) || !issuedTo || !isValid(issuedTo)) {
      return t("qtg.export.invoices.required", "Both dates are required");
    }
    if (startOfDay(issuedFrom) > new Date()) {
      return t(
        "qtg.export.invoices.inFuture",
        "The date must not be in the future",
      );
    }
    if (startOfDay(issuedTo) < startOfDay(issuedFrom)) {
      return t(
        "qtg.export.invoices.toBeforeFrom",
        "The end date must not be before the start date",
      );
    }
    if (startOfDay(issuedTo) > getMaxIssuedTo(issuedFrom)) {
      return t("qtg.export.invoices.tooLong", {
        months: MAX_EXPORT_PERIOD_MONTHS,
        defaultValue: "The period must not be longer than {{months}} month",
      });
    }
    return null;
  }, [issuedFrom, issuedTo, t]);

  const formatDate = (date?: Date) =>
    date && isValid(date) ? format(date, "dd.MM.yyyy") : "";

  return (
    <Dialog open={open} onOpenChange={(e, data) => !data.open && onCancel()}>
      <DialogSurface style={{ maxWidth: "460px" }}>
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                aria-label="close"
                icon={<DismissRegular />}
                onClick={onCancel}
              />
            }
          >
            {t("qtg.export.invoices.title", "Export invoices")}
          </DialogTitle>
          <DialogContent>
            <p style={{ marginTop: 0 }}>
              {t("qtg.export.invoices.description", {
                days: DEFAULT_EXPORT_DAYS,
                defaultValue:
                  "Exports every MTPL, CASCO and group invoice issued in the chosen period (both days included) to an Excel file for the Premium Collection Tool. The last {{days}} days are offered. Line invoices of grouped offers are covered by their group invoice.",
              })}
            </p>
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
              <Field
                data-help="offers-export-invoices-from"
                label={t("qtg.export.invoices.issuedFrom", "Issued from")}
                required
                size="small"
              >
                <DatePicker
                  value={issuedFrom}
                  onSelectDate={(date) =>
                    setIssuedFrom(date && isValid(date) ? date : null)
                  }
                  formatDate={formatDate}
                  placeholder="DD.MM.YYYY"
                  style={{ width: "160px" }}
                  size="small"
                />
              </Field>
              <Field
                data-help="offers-export-invoices-to"
                label={t("qtg.export.invoices.issuedTo", "Issued to")}
                required
                size="small"
              >
                <DatePicker
                  value={issuedTo}
                  onSelectDate={(date) =>
                    setIssuedTo(date && isValid(date) ? date : null)
                  }
                  formatDate={formatDate}
                  placeholder="DD.MM.YYYY"
                  style={{ width: "160px" }}
                  size="small"
                />
              </Field>
            </div>
            {validationError && (
              <div
                style={{
                  marginTop: 12,
                  padding: 8,
                  borderRadius: 4,
                  background: theme.semanticColors.errorBackground,
                  color: theme.semanticColors.errorText,
                }}
              >
                {validationError}
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onCancel}>
              {t("qtg.common.cancel", "Cancel")}
            </Button>
            <Button
              data-help="offers-export-invoices-confirm"
              appearance="primary"
              disabled={!!validationError}
              onClick={() => {
                if (issuedFrom && issuedTo && !validationError) {
                  onConfirm({ issuedFrom, issuedTo });
                }
              }}
            >
              {t("qtg.export.period.confirm", "Export")}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
